import responsiveImageStyles from '../config/ResponsiveImageStyles';

const mediaQueries = {
  smallMobile: 'all and (max-width: 30em)',
  mobile: 'all and (max-width: 59.999em)',
  tablet: 'all and (min-width: 60em)',
  desktop: 'all and (min-width: 80em)',
};

const getObjectKey = (object, keyIndex = 0) => {
  return Object.keys(object)[`${keyIndex}`];
};

const getObjectValue = (object, keyIndex = 0) => {
  return Object.values(object)[`${keyIndex}`];
};

const createImageStylePath = (imageStyle, imagePath, isDamImage = false) => {
  if (isDamImage) {
    let [width, height] = imageStyle.split('x');
    // Dam images could be not more than 2048px by any of the sides.
    const biggerSide = width >= height ? width : height;
    if (biggerSide > 2048) {
      const scaleFactor = biggerSide / 2048;
      width = Math.round(width / scaleFactor);
      height = Math.round(height / scaleFactor);
    }
    return `${imagePath}?crop=yes&k=c&w=${width}&h=${height}&itok=DnHkkNQG`;
  }
  return `/sites/default/files/styles/${imageStyle}/public/${imagePath}`;
};

const getDummyImage = (imageSize) => {
  return `https://source.unsplash.com/random/${imageSize}/?tech`;
};

const formatUrl = (url) => {
  if (!url) {
    return '';
  }
  const urlParts = url.split('/');
  if (urlParts.length < 1) {
    return '';
  }
  let indexToCutfrom = urlParts.length - 2;
  if (urlParts[4] && urlParts[4] === 'youtube_thumbnails') {
    indexToCutfrom -= 1;
  }
  return `${urlParts
    .slice(Math.max(indexToCutfrom, 1))
    // @ToDo should we use same itok always?
    .join('/')}?itok=38iJz1Pa`;
};

const clearParameters = (url) => {
  if (!url) {
    return '';
  }
  const urlObj = new URL(url);
  return `${urlObj.origin}${urlObj.pathname}`;
};

const createImageStyle = (imageStyle, imagePath, damImage, imageSize) => {
  if (!imageStyle || (!window.isStoryBook && !imagePath && !damImage)) {
    return false;
  }
  const isDamImage = !imagePath && damImage;
  const setParameter =
    window.isStoryBook || isDamImage ? getObjectValue : getObjectKey;
  const renderPath = window.isStoryBook ? getDummyImage : createImageStylePath;
  const formattedPath = !isDamImage
    ? formatUrl(imagePath)
    : clearParameters(damImage);

  return renderPath(
    setParameter({ [imageStyle]: imageSize }),
    formattedPath,
    isDamImage,
  );
};

const generateSources = (
  responsiveImageStyle,
  renderPath,
  setParameter,
  formattedPath,
  isDamImage,
  generateDefault = true,
) => {
  return Object.entries(responsiveImageStyles[`${responsiveImageStyle}`]).map(
    ([breakpoint, multipliers]) => {
      if (mediaQueries[`${breakpoint}`]) {
        const { x1, x2 } = multipliers;
        let source = renderPath(setParameter(x1), formattedPath, isDamImage);

        if (x2) {
          const x2Source = renderPath(
            setParameter(x2),
            formattedPath,
            isDamImage,
          );
          source = `${source} 1x, ${x2Source} 2x`;
        }

        return {
          srcSet: source,
          media: mediaQueries[`${breakpoint}`],
        };
      }
      if (generateDefault && breakpoint === 'default') {
        return renderPath(setParameter(multipliers), formattedPath, isDamImage);
      }

      return '';
    },
  );
};

const createResponsiveImageStyles = (
  responsiveImageStyle,
  imagePath,
  damImage,
  urlWebp,
) => {
  if (
    !responsiveImageStyle ||
    !Object.prototype.hasOwnProperty.call(
      responsiveImageStyles,
      responsiveImageStyle,
    ) ||
    (!window.isStoryBook && !imagePath && !damImage)
  ) {
    return false;
  }
  const isDamImage = !imagePath && damImage;
  const setParameter =
    window.isStoryBook || isDamImage ? getObjectValue : getObjectKey;
  const renderPath = window.isStoryBook ? getDummyImage : createImageStylePath;
  let formattedPath = !isDamImage
    ? formatUrl(imagePath)
    : clearParameters(damImage);

  let responsiveImageStylesOutput = generateSources(
    responsiveImageStyle,
    renderPath,
    setParameter,
    formattedPath,
    isDamImage,
  );

  if (urlWebp) {
    formattedPath = !isDamImage ? formatUrl(urlWebp) : clearParameters(urlWebp);
    responsiveImageStylesOutput = responsiveImageStylesOutput.concat(
      generateSources(
        responsiveImageStyle,
        renderPath,
        setParameter,
        formattedPath,
        isDamImage,
        false,
      ),
    );
  }

  return responsiveImageStylesOutput;
};

export {
  mediaQueries,
  createResponsiveImageStyles,
  createImageStylePath,
  getDummyImage,
  createImageStyle,
  clearParameters,
};
